/* eslint-disable react-native/no-inline-styles */

import React, { useEffect, useRef } from 'react';
import { Text } from 'react-native';
import { DocketStatus, KdsTimerMilestone } from '@oolio-group/domain';
import {
  getTimeDifference,
  minifyHourText,
  minifyMinuteText,
  convertToMinutes,
  getTimeDifferenceInMinutes,
} from '../../../utils/timeHelper';
import moment from 'moment';
import DocketItemStyles from '../DocketItem.styles';
import { useSession } from '../../../hooks/useSession';
import { stripProperties } from '@oolio-group/client-utils';
import {
  bulkSelectVar,
  selectedDocketVar,
  timeVar,
} from '../../../state/apolloVars';
import { useReactiveVar } from '@apollo/client/react/hooks';

import { BehaviorSubject } from 'rxjs';

export const headerColorSubject = new BehaviorSubject<Record<string, string>>(
  {},
);

export interface DocketItemTimerProps {
  createdTime: string;
  status: DocketStatus;
  docketId: string;
}

const DocketItemTimer: React.FC<DocketItemTimerProps> = ({
  createdTime,
  status,
  docketId,
}) => {
  const time = useReactiveVar<number>(timeVar);
  const isBulkMode = useReactiveVar(bulkSelectVar);
  const selectedDockets = useReactiveVar(selectedDocketVar);
  const isSelected = Object.keys(selectedDockets).includes(docketId);

  const styles = DocketItemStyles({ isSelected: isBulkMode && isSelected });

  const { session } = useSession();
  const lastMilestone = useRef<string | null>(null);

  const milestones = (session?.kitchenDisplay?.timerMilestones || []).map(
    (x: KdsTimerMilestone) => stripProperties(x, '__typename'),
  ) as KdsTimerMilestone[];

  useEffect(() => {
    if (status === DocketStatus.CREATED) {
      if (!milestones.length) {
        const isEmpty = Object.keys(headerColorSubject.getValue()).length === 0;
        if (!isEmpty) {
          headerColorSubject.next({});
        }
        return;
      }
      const timeDiff = getTimeDifferenceInMinutes(time, Number(createdTime));
      const applicableMilestone = milestones
        .filter(m => timeDiff > m.minutes)
        .reduce((prev, curr) => (timeDiff > curr.minutes ? curr : prev), {
          label: '',
          minutes: 0,
          color: '',
        });

      const colorToApply =
        applicableMilestone.minutes > 0 ? applicableMilestone.color : null;

      if (colorToApply !== lastMilestone.current) {
        lastMilestone.current = colorToApply;

        const updatedColors = { ...headerColorSubject.getValue() };

        if (!!colorToApply) {
          updatedColors[docketId] = colorToApply;
        } else {
          delete updatedColors[docketId];
        }

        headerColorSubject.next(updatedColors);
      }
    }
  }, [status, time, createdTime, docketId, milestones]);

  const duration =
    status === DocketStatus.CREATED
      ? convertToMinutes(
          getTimeDifference(time, Number(createdTime)) || '0:00:00',
        )
      : (() => {
          const timeFromNow = moment(createdTime).fromNow();
          const minifiedHour = minifyHourText(timeFromNow);
          const minifiedMinute = minifyMinuteText(minifiedHour);
          return minifiedMinute;
        })();

  const formattedCreatedTime = moment(createdTime).format('HH:mm');

  return (
    <Text
      testID="docket-duration"
      style={[
        styles.text,
        {
          flexShrink: 0, // Prevent shrinking
          textAlign: 'right',
        },
      ]}
    >
      {`${duration} | ${formattedCreatedTime}`}
    </Text>
  );
};

export default DocketItemTimer;
